import { getProviderJson, postProviderJson, providerError } from './providerHttp';
import { streamProviderSse, canStream } from '../providerStream';
import {
  createOpenAiAssembly,
  pushOpenAiEvent,
  unfinishedOpenAiCalls,
} from '../openaiStreamAssembly';

/**
 * OpenAI Chat Completions adapter (BYO key).
 *
 * The engine hands every adapter the same Anthropic-shaped history (the
 * internal lingua franca — the panel already speaks it); this adapter
 * translates to OpenAI's chat format on the way out and back to the shared
 * callback surface on the way in:
 *
 *   Anthropic                      →  OpenAI
 *   system string                  →  {role:'system'} message
 *   user string content            →  {role:'user'} message
 *   user tool_result blocks        →  one {role:'tool', tool_call_id} message per block
 *   assistant text + tool_use      →  {role:'assistant', content, tool_calls:[...]}
 *   tool input object              →  function.arguments (JSON string!)
 *   tool input_schema              →  function.parameters
 *
 * finish_reason mapping: 'tool_calls' → 'tool_use', 'length' → 'max_tokens',
 * everything else → 'end_turn' (the panel only branches on those three).
 * Non-streamed on purpose — see providerHttp.js.
 */

const API_URL = 'https://api.openai.com/v1/chat/completions';
const MODELS_URL = 'https://api.openai.com/v1/models';

function headers(apiKey) {
  return { authorization: `Bearer ${apiKey}` };
}

/** Anthropic tool_result content is a string in this app; be tolerant of block arrays anyway. */
function toolResultText(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => (typeof b === 'string' ? b : b?.text || ''))
      .join('');
  }
  return content == null ? '' : JSON.stringify(content);
}

export function toOpenAiMessages(system, messages) {
  const out = [{ role: 'system', content: system }];
  for (const msg of messages || []) {
    const { role, content } = msg;
    if (typeof content === 'string') {
      out.push({ role, content });
      continue;
    }
    if (role === 'user') {
      // A user turn with blocks is a tool_result turn in this app's history.
      // OpenAI wants one role:'tool' message per result, keyed by the call id.
      for (const block of content || []) {
        if (block.type === 'tool_result') {
          out.push({
            role: 'tool',
            tool_call_id: block.tool_use_id,
            content: toolResultText(block.content),
          });
        }
      }
      continue;
    }
    // Assistant turn with blocks: text + tool_use.
    const text = (content || [])
      .filter((b) => b.type === 'text')
      .map((b) => b.text)
      .join('');
    const toolCalls = (content || [])
      .filter((b) => b.type === 'tool_use')
      .map((b) => ({
        id: b.id,
        type: 'function',
        function: { name: b.name, arguments: JSON.stringify(b.input || {}) },
      }));
    const entry = { role: 'assistant', content: text || null };
    if (toolCalls.length > 0) entry.tool_calls = toolCalls;
    out.push(entry);
  }
  return out;
}

/**
 * OpenAI rejects a tool description longer than 1024 characters outright:
 *
 *   400 invalid_request_error / string_above_max_length —
 *   "Invalid 'tools[N].function.description': string too long.
 *    Expected a string with maximum length 1024"
 *
 * The tool list goes out on EVERY request, so one over-long description is not
 * a degraded tool — it is a dead provider. `generate_pre_request_script` ships
 * at 1999 characters (its description carries the whole pm.* pre-request API
 * contract), which meant no OpenAI turn could run at all.
 *
 * The clamp lives here rather than in toolSchemas.js on purpose: the limit is
 * OpenAI's, not ours, and Anthropic and Gemini both take the full text — which
 * is worth keeping, because that description is what stops the model writing a
 * pre-request script against an API that does not exist.
 */
export const OPENAI_DESCRIPTION_LIMIT = 1024;

/** Clamp at a sentence boundary where possible — a cut mid-clause reads as a
 *  contradiction to the model ("pm.sendRequest is not sup"). */
export function clampDescription(text, limit = OPENAI_DESCRIPTION_LIMIT) {
  const description = String(text ?? '');
  if (description.length <= limit) return description;

  const head = description.slice(0, limit);
  const lastStop = Math.max(head.lastIndexOf('. '), head.lastIndexOf('.\n'));
  // Only honour a sentence break if it keeps most of the budget; otherwise a
  // single early period would throw away almost the whole description.
  return lastStop > limit * 0.6 ? head.slice(0, lastStop + 1) : head;
}

export function toOpenAiTools(tools) {
  return (tools || []).map((t) => ({
    type: 'function',
    function: {
      name: t.name,
      description: clampDescription(t.description),
      parameters: t.input_schema,
    },
  }));
}

/**
 * Models that REQUIRE `reasoning_effort: 'none'` before they will accept tools
 * on /v1/chat/completions (verified against the live API, 18.08.2026).
 *
 * The whole gpt-5.6 line answers a tool-carrying request with
 * "Function tools with reasoning_effort are not supported for <model> in
 * /v1/chat/completions. To use function tools, use /v1/responses or set
 * reasoning_effort" — a 400 for the WHOLE request. Since the tool list goes out
 * on every turn, that is total failure, not a degraded one: gpt-5.6-sol is the
 * default model, so OpenAI was unusable for every new user of it.
 *
 * The flag is not free — it turns reasoning off on a reasoning model. Tools are
 * not optional for this product and reasoning is a bonus, so this is the right
 * trade for now; moving to /v1/responses would keep both and is the real fix.
 *
 * Prefix-matched rather than listed exactly, because Settings has a free-text
 * model field: a user can type a model that shipped after this code did.
 */
const REASONING_MODEL = /^(gpt-5|o[1-9])/i;

/** Error text OpenAI returns when a model does not know the flag at all. */
const UNKNOWN_FLAG = /unrecognized request argument supplied: reasoning_effort/i;
/** …and when a model needs it and did not get it. */
const FLAG_REQUIRED = /function tools with reasoning_effort are not supported/i;

/**
 * POST a chat completion, correcting the one argument whose requirement varies
 * by model.
 *
 * Both directions are handled, and both are needed because of that free-text
 * model field: gpt-4o-mini rejects the flag outright ("Unrecognized request
 * argument"), while gpt-5.6-* rejects its absence. Guessing from the name gets
 * today's models right; the retry gets tomorrow's right without a release.
 */
async function postChat(apiKey, body) {
  const wantsFlag = REASONING_MODEL.test(String(body.model || ''));
  const first = wantsFlag ? { ...body, reasoning_effort: 'none' } : body;

  let result = await postProviderJson(API_URL, headers(apiKey), first);
  if (result.status < 400) return result;

  const message = result.json?.error?.message || '';
  if (wantsFlag && UNKNOWN_FLAG.test(message)) {
    result = await postProviderJson(API_URL, headers(apiKey), body);
  } else if (!wantsFlag && FLAG_REQUIRED.test(message)) {
    result = await postProviderJson(API_URL, headers(apiKey), { ...body, reasoning_effort: 'none' });
  }
  return result;
}

export function mapStopReason(choice) {
  if ((choice?.message?.tool_calls || []).length > 0) return 'tool_use';
  if (choice?.finish_reason === 'length') return 'max_tokens';
  return 'end_turn';
}

/**
 * Map one buffered chat-completion body (`{ choices, usage }`) onto the shared
 * callback surface and return the stop reason. Shared with the Apiboo adapter
 * (providers/apiboo.js), whose server relays OpenRouter's `choices[0]`
 * verbatim — one translation, not two that drift.
 */
export function emitOpenAiChoice(json, { onTextDelta, onToolUse, onUsage } = {}) {
  if (json?.usage) {
    onUsage?.({
      input_tokens: json.usage.prompt_tokens || 0,
      output_tokens: json.usage.completion_tokens || 0,
    });
  }
  const choice = (json?.choices || [])[0] || {};
  const message = choice.message || {};
  if (message.content) onTextDelta?.(message.content);
  for (const tc of message.tool_calls || []) {
    let input = {};
    try {
      input = JSON.parse(tc.function?.arguments || '{}');
    } catch {
      input = {};
    }
    onToolUse?.({ id: tc.id, name: tc.function?.name, input });
  }
  return { stopReason: mapStopReason(choice) };
}

export async function send({ apiKey, model, system, reference, tools, messages, onTextDelta, onToolUse, onUsage }) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const body = {
    model,
    messages: toOpenAiMessages(fullSystem, messages),
    tools: toOpenAiTools(tools),
  };

  const { status, json } = await postChat(apiKey, body);
  if (status >= 400 || !json) {
    throw providerError('OpenAI', status, json?.error?.message);
  }

  return emitOpenAiChoice(json, { onTextDelta, onToolUse, onUsage });
}

/**
 * Validate the key (and that it can see this model) by READING the model back.
 *
 * This used to send a completion with `max_completion_tokens: 1`. On the GPT-5
 * family that is a guaranteed failure regardless of the key: they are reasoning
 * models, reasoning tokens count against the completion budget, so a budget of
 * 1 is spent before any message exists and the API answers 400 "Could not
 * finish the message because max_tokens or model output limit was reached".
 * A perfectly good, funded key looked broken. See providerHttp.getProviderJson.
 */
/** Whether this adapter can stream in the current runtime. */
export function supportsStreaming() {
  return canStream();
}

/**
 * Stream a turn.
 *
 * `reasoning_effort` is applied here the same way `postChat` applies it, but
 * WITHOUT the retry: a stream that has already delivered tokens cannot be
 * replayed, so the flag is decided up front from the model name. A model that
 * rejects it falls back to the buffered path via the thrown error, which is the
 * honest outcome — better than silently losing the first half of a reply.
 */
export async function sendStreaming({
  apiKey, model, system, reference, tools, messages,
  onTextDelta, onToolUse, onUsage, onStart,
}) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const body = {
    model,
    messages: toOpenAiMessages(fullSystem, messages),
    tools: toOpenAiTools(tools),
    stream: true,
    // Usage is not reported on a stream unless it is asked for.
    stream_options: { include_usage: true },
    ...(REASONING_MODEL.test(String(model || '')) ? { reasoning_effort: 'none' } : {}),
  };

  const state = createOpenAiAssembly();
  let delivered = false;

  // Observed live on 18.08.2026: gpt-5.6-sol answers a STREAMED tool request
  // with a 500 ("The server had an error processing your request… You can
  // retry") roughly two times in three. The buffered path is stable and the
  // other models are stable, so this is OpenAI-side instability on one model,
  // not our request being wrong.
  //
  // Retrying is only safe because of WHEN it fails: streamProviderSse rejects
  // from ai_stream_start before a single chunk is emitted when the HTTP status
  // is bad, so `delivered` is still false and nothing has reached the user.
  // A failure after tokens have arrived is NOT retried — that would replay half
  // a reply and re-run whatever the first attempt already did.
  const attempt = () => streamProviderSse(API_URL, headers(apiKey), body, (payload) => {
    delivered = true;
    const out = pushOpenAiEvent(state, payload);
    if (out.text) onTextDelta?.(out.text);
    // All at once, at finish_reason — this protocol has no per-call signal.
    for (const call of out.toolUses || []) onToolUse?.(call);
    if (out.usage) onUsage?.(out.usage);
  }, { onStart });

  try {
    await attempt();
  } catch (err) {
    if (delivered || !/\b5\d\d\b/.test(String(err?.message || ''))) throw err;
    await attempt();
  }

  const unfinished = unfinishedOpenAiCalls(state);
  if (unfinished.length) {
    throw new Error(
      `The response was cut off while calling ${unfinished.map((t) => t.name).filter(Boolean).join(', ') || 'a tool'}. Nothing was run — ask again.`
    );
  }

  return { stopReason: state.stopReason || 'end_turn' };
}

export async function testKey({ apiKey, model }) {
  const url = `${MODELS_URL}/${encodeURIComponent(model)}`;
  const { status, json } = await getProviderJson(url, headers(apiKey));
  if (status >= 400 || !json) {
    throw providerError('OpenAI', status, json?.error?.message);
  }
  return true;
}
